// src/components/FormSettings/PreviewFields.jsx
// May8 PreviewFields without banner fix (revised for parity + attach debug)
import React, { useRef, useEffect } from "react";
import { Box } from "@mui/material";
import FieldRenderer from "../Fields/FieldRenderer";
import { useFormLayout } from "../../contexts/FormLayoutContext"; // Ensure consistency with FormLayoutContext
import { useFieldMetadata } from "../../contexts/FieldMetadataContext"; // Import metadata context

import { useFormAnswers } from "../../contexts/FormAnswersContext"; 
/**
 * Mirror the builder's computeWrapperStyle(meta) semantics exactly:
 *  - icon   → no grow/shrink, basis auto (tight content)
 *  - banner → width: 100% (full row)
 *  - header → vertically centered within the row
 *  - other  → grow + shrink, basis 0 (equal distribution with siblings)
 */
function computePreviewWrapperStyle(meta = {}) {
  const isIcon   = meta?.allowedImageTypes?.includes?.("icon");
  const isBanner = meta?.allowedImageTypes?.includes?.("banner");
  const isHeader = meta?.type === "header";

  return {
    display    : "flex",
    // Match RowComponent: center header vertically, stretch others
    alignItems : isHeader ? "center" : "stretch",
    minWidth   : 0,             // ← allow this flex item (column) to shrink
    flexGrow   : isIcon ? 0 : 1,
    flexShrink : isIcon ? 0 : 1,
    flexBasis  : isIcon ? "auto" : 0,
    ...(isBanner ? { width: "100%" } : null), // banner special-case
  };
}


/**
 * A small wrapper component so we can attach a ref and log computed widths for attach fields.
 * This helps verify that nothing else is constraining the width (e.g., parent, sibling flex, etc.).
 */
function PreviewFieldItem({
  rowKey,
  field,
  fieldData,
  fieldValue,
  textAlignValue,
  spacingStyles,
  viewOnly,
}) {
  const wrapperRef = useRef(null);

  const isAttach    = fieldData?.type === "attach";
  const isIconField = fieldData?.allowedImageTypes?.includes("icon");
  const isHeader    = fieldData?.type === "header";

  // ────────────────────────────────────────────────────────────────
  // AUF Flicker Diagnostics: does this wrapper REMOUNT?
  // If yes -> key instability higher up (or conditional rendering)
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__DEBUG_AUF_FLICKER__ !== true) return;
    if (!(isAttach || fieldData?.type === "image")) return;

    console.log("[AUF:Flicker][PreviewFieldItem] MOUNT", {
      rowKey,
      fieldId: field.id,
      type: fieldData?.type,
    });

    return () => {
      console.log("[AUF:Flicker][PreviewFieldItem] UNMOUNT", {
        rowKey,
        fieldId: field.id,
        type: fieldData?.type,
      });
    };
  }, [rowKey, field.id, isAttach, fieldData?.type]);

  // 🔹 Normalize the type for preview/final:
  //    - For asset-type fields, fieldData.type is already "single-select"
  //    - For normal fields, it's just the same as field.type
  const effectiveType = fieldData?.type || field.type;

  const wrapperStyle = computePreviewWrapperStyle(fieldData);


  // Debug log to verify final widths for attach
  useEffect(() => {
    if (!isAttach || !wrapperRef.current) return;

    const el = wrapperRef.current;
    const parent = el.parentElement;
    const row = parent?.parentElement;

    const msg = {
      rowKey,
      fieldId: field.id,
      wrapperFlex: getComputedStyle(el).flex,
      wrapperClientWidth: el.clientWidth,
      parentClientWidth: parent?.clientWidth,
      rowClientWidth: row?.clientWidth,
      parentAlignItems: getComputedStyle(parent || el).alignItems,
      rowAlignItems: getComputedStyle(row || el).alignItems,
      // Note: if any ancestor is limiting width via max-width or fixed width, it will show up here
      wrapperComputedWidth: getComputedStyle(el).width,
    };

    // eslint-disable-next-line no-console
//    console.log("[PreviewFields][attach] width debug →", msg);
  }, [isAttach, rowKey, field.id]);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        ...wrapperStyle,
        textAlign: textAlignValue,
        ...spacingStyles,
        // NOTE: this is the builder-equivalent *wrapper*. The child below is the "inner stretcher".
      }}
    >
      {/* Inner stretcher – mirrors the builder’s div with flexGrow:1 in DraggableItem */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          display: "flex",
          // Match DraggableItem: headers should not stretch vertically
          alignItems: isHeader ? "flex-start" : "stretch",
          height:    isHeader ? "auto"       : "100%",
        }}
      >
        <FieldRenderer
          id={field.id}
          type={effectiveType}
          label={fieldData.label}
          required={fieldData.required}
          index={field.index || 0}
          mode="final"
          propValue={fieldValue}
          fieldMetadata={fieldData}
          viewOnly={viewOnly}
        />
      </Box>


    </Box>
  );
}

const PreviewFields = React.memo(({
  fieldsMetadata,
  layoutConfig,
  includeSubmitRow = false,
  viewOnly = false,
  // OPTIONAL: runtime values (AUF / published form runtime)
  // When provided, this makes PreviewFields behave like a real "final" renderer.
  valuesByUfi = null,
}) => {
  const { layoutConfig: formLayoutConfig } = useFormLayout(); // Ensure consistency with FormLayoutContext

  // ────────────────────────────────────────────────────────────────
  // FINAL runtime values (enterprise)
  // - PreviewFields used to always pass fieldData.defaultValue into FieldRenderer.
  // - That breaks AUF: attach/image values are runtime answers, not defaults.
  // - We read FormAnswersContext here (if present) and only fall back to defaultValue.
  // ────────────────────────────────────────────────────────────────
  const { getAnswer, answersByUfi } = useFormAnswers() || {};

  // Helper to resolve value for a field:
  // - Prefer valuesByUfi (explicit override)
  // - Else prefer FormAnswersContext (answersByUfi / getAnswer)
  // - Else fallback to metadata.defaultValue
  const resolveRuntimeValue = (fieldMeta, fallbackDefault) => {
    const ufi = fieldMeta?.ufi || fieldMeta?.id || null;
    if (!ufi) return fallbackDefault;

    // 1) explicit override map
    if (valuesByUfi && Object.prototype.hasOwnProperty.call(valuesByUfi, ufi)) {
      return valuesByUfi[ufi];
    }

    // 2) live answers map (fast)
    if (answersByUfi && Object.prototype.hasOwnProperty.call(answersByUfi, ufi)) {
      return answersByUfi[ufi];
    }

    // 3) function fallback
    if (typeof getAnswer === "function") {
      const v = getAnswer(ufi);
      if (v !== undefined) return v;
    }

    return fallbackDefault;
  };

  // ────────────────────────────────────────────────────────────────
  // AUF Flicker Diagnostics (LOG ONLY)
  // Enable in DevTools:
  //   window.__DEBUG_AUF_FLICKER__ = true
  // ────────────────────────────────────────────────────────────────
  const __DBG__ =
    typeof window !== "undefined" && window.__DEBUG_AUF_FLICKER__ === true;

  const renderTickRef = React.useRef(0);
  renderTickRef.current += 1;

  if (__DBG__) {
    console.log("[AUF:Flicker][PreviewFields] render", {
      tick: renderTickRef.current,
      rows: layoutConfig ? Object.keys(layoutConfig).length : 0,
      metaKeys: fieldsMetadata ? Object.keys(fieldsMetadata).length : 0,
      includeSubmitRow,
      viewOnly,
    });
  }

  if (!fieldsMetadata || Object.keys(fieldsMetadata).length === 0) {
    console.warn("⚠️ [PreviewFields] fieldsMetadata is empty. Skipping render.");
    return null;
  }

//  console.log("[PreviewFields] Metadata received in PreviewFields:", fieldsMetadata);
//  console.log("[PreviewFields] Received layoutConfig:", layoutConfig);

  return (
    <>
      {Object.keys(layoutConfig).map((rowKey) =>
        (includeSubmitRow || rowKey !== "submitRow") ? (

          <Box
            key={rowKey}
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: formLayoutConfig?.rowSpacing || 1,
              marginBottom: formLayoutConfig?.rowMargin || 1,
              alignItems: "stretch",  //  match builder row behavior
              width: "100%",          //  row claims full width (like builder)
              flexWrap: "nowrap",
              minWidth: 0,            // ← ensure row lets its children shrink
            }}
          >
            {layoutConfig[rowKey].map((field) => {
              const fieldData  = fieldsMetadata[field.id] || field;

              // ────────────────────────────────────────────────────────────────
              // ENTERPRISE: runtime values (AUF/published)
              // - Prefer runtime answer (UFI) over defaultValue
              // - For attach/image: if runtime answer is missing, pass undefined
              //   so FieldRenderer does NOT overwrite local attach state with ""
              // ────────────────────────────────────────────────────────────────
              const fallbackDefault =
                (fieldData?.defaultValue !== undefined) ? fieldData.defaultValue : "";

              const runtimeValue = resolveRuntimeValue(fieldData, fallbackDefault);

              const isAttachOrImage =
                fieldData?.type === "attach" || fieldData?.type === "image";

              // For attach/image: only pass a value if it exists in runtime layer;
              // otherwise allow FieldRenderer + AttachField to keep local state.
              const ufiForValue = fieldData?.ufi || field.id;
              const hasExplicitRuntime =
                (valuesByUfi && Object.prototype.hasOwnProperty.call(valuesByUfi, ufiForValue)) ||
                (answersByUfi && Object.prototype.hasOwnProperty.call(answersByUfi, ufiForValue));

              const fieldValue =
                isAttachOrImage
                  ? (hasExplicitRuntime ? runtimeValue : undefined)
                  : runtimeValue;

              // ────────────────────────────────────────────────────────────────
              // AUF Flicker Diagnostics: confirm attach value source + key stability
              // ────────────────────────────────────────────────────────────────
              if (__DBG__ && isAttachOrImage) {
                console.log("[AUF:Flicker][PreviewFields] attach/image slot", {
                  rowKey,
                  reactKey: field.id,              // what you currently use
                  fieldId: field.id,
                  fieldType: fieldData?.type,
                  ufi: ufiForValue,
                  valueSource: hasExplicitRuntime
                    ? "runtime-answer"
                    : (fieldData?.defaultValue !== undefined ? "defaultValue" : "(fallback-empty)"),
                  runtimeType:
                    (fieldValue == null)
                      ? String(fieldValue)
                      : (Array.isArray(fieldValue) ? "array" : typeof fieldValue),
                  hasFile: Boolean(fieldValue && fieldValue.file),
                  // helps detect "same data, new object" churn
                  fieldDataSameAsLayoutField: fieldData === field,
                  fieldDataKeys: fieldData ? Object.keys(fieldData) : [],
                });
              }
              // 🔹 Capture `textAlign` specifically for header fields
              const textAlignValue = fieldData?.type === 'header'
                ? fieldData?.style?.textAlign || "center"
                : fieldData?.style?.textAlign || undefined;

//              console.log(
//                `[PreviewFields] Field ID: ${field.id}, Type: ${field.type}, ` +
//                `Detected textAlign: ${textAlignValue}`
//              );

              // parity with builder's margins/padding rules
              const isIconField = fieldData?.allowedImageTypes?.includes("icon");
              const spacingStyles = {
                marginLeft  : isIconField ? "16px" : fieldData?.style?.marginLeft  || "0px",
                marginRight : isIconField ? "16px" : fieldData?.style?.marginRight || "0px",
                paddingTop    : fieldData?.style?.paddingTop    || "0px",
                paddingBottom : fieldData?.style?.paddingBottom || "0px",
              };

              return (
                <PreviewFieldItem
                  key={field.id}
                  rowKey={rowKey}
                  field={field}
                  fieldData={fieldData}
                  fieldValue={fieldValue}
                  textAlignValue={textAlignValue}
                  spacingStyles={spacingStyles}
                  viewOnly={viewOnly}
                />
              );
            })}
          </Box>
        ) : null
      )}
    </>
  );
});

export default PreviewFields;
